import { useQuery, useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { Layout } from "providers/Layout";
import { Link, useParams } from "react-router-dom";
import { Redirect } from "shared/components/Redirect";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { AlertCircle, Clock, Edit05, Link01, Mail01 } from "untitledui-js-base";
import { ActivityLogDrawer } from "./components/ActivityLog";
import { useState } from "react";
import { Loader } from "shared/components/Loader";
import { Subscriber } from "./types";

import { getErrorMessage } from "utils/error";
import { SubscriptionStatusBadge } from "./components/SubscriptionStatusBadge";
import { AccountStatusBadge } from "views/rent-reporting/components/AccountStatusBadge";
import {
  AccountStatusVariant,
  CaseStatusVariant
} from "views/rent-reporting/constants";
import { NotificationBanner } from "./components/NotificationBanner";
import { useAuthContext } from "providers/Auth";
import { toast } from "react-toastify";
import { handleCopy } from "utils/handleCopy";
import { Tabs } from "shared/components/Tabs";
import { RentReportingTabs } from "./constants";
import { SubscriptionInfo } from "./slices/SubscriptionInfo";
import { PmsLedgerTable } from "./slices/PmsLedgerTable";
import { getDisplayAccountStatus } from "views/rent-reporting/utils";
import { formatDate } from "./slices/SubscriptionInfo/slices/RentReportingTable/utils";

export const SubscriptionDetails = () => {
  const [showActivityLog, setShowActivityLog] = useState(false);
  const { renterId } = useParams();
  const { type, permissions } = useAuthContext();
  const [activeTab, setActiveTab] = useState<string>(
    RentReportingTabs.Subscription
  );

  const {
    data: renter,
    isFetching,
    isError,
    error
  } = useQuery({
    queryKey: ["SUBSCRIBER_DETAIL", renterId],
    queryFn: () =>
      instance
        .get(`/portal/v1/customers/${renterId}`)
        .then(({ data }: { data: Subscriber }) => data)
  });

  const handleCopyVerificationLink = async () => {
    try {
      await navigator.clipboard.writeText(renter?.verification_link || "");

      toast.info("Link copied");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleResendEmail = useMutation({
    mutationKey: ["RESEND_EMAIL", renter?.property?.id],
    mutationFn: async (emailType: "opt_in" | "opt_out") => {
      return instance.post(`/portal/v1/customers/${renterId}/resend_email`, {
        property_id: renter?.property?.id,
        email_type: emailType
      });
    },
    onSuccess: () => {
      toast.success(`Email resent successfully`);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const {
    data,
    isLoading,
    isError: logsError
  } = useQuery({
    queryKey: ["AUDIT_LOGS", renterId],
    queryFn: () =>
      instance
        .get(`/portal/v1/customers/${renterId}/audit_logs`)
        .then(({ data }) => data),
    enabled: Boolean(renterId)
  });

  const pendingAccount =
    renter?.account_status === AccountStatusVariant.Pending;

  if (!permissions.canViewRentReporting) return <Redirect href="/" />;

  if (isError)
    return (
      <Layout>
        <div>
          <h1 className="mb-4 text-lg font-medium">
            Error loading subscriber details.
          </h1>
          <p className="text-base font-medium">{error.message}</p>
        </div>
      </Layout>
    );

  if (isFetching)
    return (
      <Layout
        headerClassName="py-0"
        header={
          <div className="border-b border-alpha-40 p-6">
            <BreadCrumbs
              crumbs={[
                {
                  title: "Rent reporting",
                  href: "/rent-reporting"
                }
              ]}
            />
          </div>
        }
      >
        <div className="flex items-center justify-center">
          <Loader />
        </div>
      </Layout>
    );

  const displayStatus = getDisplayAccountStatus(renter!);
  return (
    <Layout
      headerClassName="py-0"
      header={
        <>
          <div className="border-b border-alpha-40 p-6">
            <BreadCrumbs
              crumbs={[
                {
                  title: "Rent reporting",
                  href: "/rent-reporting"
                },
                {
                  title: renter?.name || "--",
                  href: `/rent-reporting/${renterId}`
                }
              ]}
            />
          </div>
          <div className="flex items-center justify-between border-b border-alpha-100 px-6 py-3">
            <div>
              <h2 className="mb-1 text-sm text-content-400">
                Enrollment Details
              </h2>
              <h2 className="text-xl font-bold text-content-1000">
                {renter?.name}
              </h2>
            </div>
            {permissions.canEditSubscriptionDetails &&
            ((renter?.account_status !== AccountStatusVariant.Unsubscribed &&
              type.isManual) ||
              type.isPMS) ? (
              <Link to={`/rent-reporting/${renterId}/edit`}>
                <Button
                  Icon={Edit05}
                  variant={ButtonVariant.BASE}
                  borderLess
                  className="stroke-primary-500 font-bold text-primary-500"
                  size={ButtonSize.SM}
                >
                  Edit Details
                </Button>
              </Link>
            ) : null}
            {renter?.account_status === AccountStatusVariant.Unsubscribed &&
            type.isManual &&
            permissions.canSubscribeRenters ? (
              <div className="flex gap-1">
                {renter.can_resubscribe ? (
                  <Link to={`/rent-reporting/${renterId}/resubscribe`}>
                    <Button
                      variant={ButtonVariant.BASE}
                      borderLess
                      className="stroke-primary-500 font-bold text-primary-500"
                      size={ButtonSize.SM}
                    >
                      Resume subscription to current lease
                    </Button>
                  </Link>
                ) : null}

                <Link to={`/rent-reporting/${renterId}/new-subscription`}>
                  <Button
                    variant={ButtonVariant.PRIMARY}
                    className="font-bold text-primary-500"
                    size={ButtonSize.SM}
                  >
                    Subscribe for new lease
                  </Button>
                </Link>
              </div>
            ) : null}
          </div>
          <div className="flex items-center justify-between p-6 border-b border-alpha-100 flex-wrap md:flex-nowrap">
            <div className="flex gap-4 items-center flex-wrap md:flex-nowrap">
              <div className="min-w-[120px] w-auto">
                <p className="mb-2 text-xs text-content-400">Account Status</p>
                {displayStatus ? (
                  <AccountStatusBadge status={displayStatus} />
                ) : (
                  "--"
                )}
              </div>
              <div className="min-w-[120px] w-auto">
                <p className="mb-2 text-xs text-content-400">
                  Enrollment created
                </p>
                <p className="text-sm font-medium text-content-1000">
                  {formatDate(renter?.active_enrollment?.created_at)}
                </p>
              </div>
              <div className="min-w-[120px] w-auto">
                <p className="mb-2 text-xs text-content-400">
                  Enrollment approved
                </p>
                <p className="text-sm font-medium text-content-1000">
                  {formatDate(renter?.active_enrollment?.approved_at)}
                </p>
              </div>
              <div className="min-w-[120px] w-auto">
                <p className="mb-2 text-xs text-content-400">
                  Unenrollment date
                </p>
                <p className="text-sm font-medium text-content-1000">
                  {formatDate(renter?.active_enrollment?.unenrollment_date)}
                </p>
              </div>
            </div>
            {type.isSDK ? null : (
              <Button
                Icon={Clock}
                size={ButtonSize.SM}
                onClick={() => {
                  setShowActivityLog(true);
                }}
              >
                Activity log
              </Button>
            )}
          </div>
        </>
      }
    >
      {pendingAccount && renter.verification_link ? (
        <NotificationBanner
          icon={<AlertCircle size="24" className="stroke-danger-600" />}
          title={`${renter.name} needs to complete manual identity verification`}
          text={`${renter.name} will receive an email with instructions to
        complete manual verification. Alternatively, you can send the verification link directly to
        the renter.`}
          button={
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              Icon={Link01}
              onClick={handleCopyVerificationLink}
            >
              Copy Verification Link
            </Button>
          }
        />
      ) : null}
      {pendingAccount &&
        renter.case_status === CaseStatusVariant.PendingOptIn &&
        renter.opt_in_link && (
          <NotificationBanner
            icon={<Mail01 size="24" className="stroke-content-100" />}
            title={`Opt-in email sent to ${renter.name}. They can opt-in at any time.`}
            text={`${renter.name} should receive an email with instructions to opt-in to rent reporting. Alternatively, you can send the opt-in link directly to the renter.`}
            button={
              <div className="flex gap-2">
                <Button
                  variant={ButtonVariant.BASE}
                  size={ButtonSize.SM}
                  onClick={() => handleCopy(renter.opt_in_link || "")}
                >
                  Copy link
                </Button>
                <Button
                  variant={ButtonVariant.PRIMARY}
                  size={ButtonSize.SM}
                  loading={handleResendEmail.isPending}
                  onClick={() => handleResendEmail.mutate("opt_in")}
                >
                  Resend email
                </Button>
              </div>
            }
          />
        )}
      {pendingAccount &&
      renter.case_status === CaseStatusVariant.PendingOptOut &&
      renter.opt_out_link &&
      (renter.remaining_opt_out_days ?? -1) >= 0 ? (
        <NotificationBanner
          icon={<Mail01 size="24" className="stroke-content-100" />}
          title={`Opt-out email sent to ${renter.name}. They have ${renter.remaining_opt_out_days} days to opt-out`}
          text={`${renter.name} should receive an email with instructions to opt-out of rent reporting. Alternatively, you can send the opt-out link directly to the renter.`}
          button={
            <div className="flex gap-2">
              <Button
                variant={ButtonVariant.BASE}
                size={ButtonSize.SM}
                onClick={() => handleCopy(renter.opt_out_link || "")}
              >
                Copy link
              </Button>
              <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SM}
                loading={handleResendEmail.isPending}
                onClick={() => handleResendEmail.mutate("opt_out")}
              >
                Resend email
              </Button>
            </div>
          }
        />
      ) : null}
      <div className="mb-6">
        <Tabs
          tabs={[
            RentReportingTabs.Subscription,
            ...(type.isPMS && permissions.canViewRentersList
              ? [RentReportingTabs.PMSLedger]
              : [])
          ]}
          displayMap={{
            [RentReportingTabs.Subscription]: "Subscription",
            [RentReportingTabs.PMSLedger]: "PMS ledger"
          }}
          variant="underline"
          selected={activeTab}
          setActiveTab={(t) => setActiveTab(t)}
        />
      </div>
      <Show if={activeTab === RentReportingTabs.Subscription}>
        <SubscriptionInfo renter={renter} />
      </Show>
      <Show if={activeTab === RentReportingTabs.PMSLedger}>
        <PmsLedgerTable renterId={renterId || ""} />
      </Show>
      <ActivityLogDrawer
        opened={showActivityLog}
        onClose={() => setShowActivityLog(false)}
        acitvityLogs={data || []}
        isLoading={isLoading}
        isError={logsError}
        residentName={renter?.name}
      />
    </Layout>
  );
};
